import { useEffect, useState } from 'react';

const DEFAULT_DELAY_MS = 800;

/**
 * Debounces search input for API queries. Clears immediately when the field is
 * emptied so results reset without waiting.
 */
export function useDebouncedSearch(value: string, delayMs = DEFAULT_DELAY_MS): string {
  const [debounced, setDebounced] = useState(() => value.trim());

  useEffect(() => {
    const trimmed = value.trim();

    if (!trimmed) {
      setDebounced('');
      return undefined;
    }

    const timeout = window.setTimeout(() => setDebounced(trimmed), delayMs);
    return () => window.clearTimeout(timeout);
  }, [value, delayMs]);

  return debounced;
}

/**
 * Search value safe for API calls. Clears immediately when the input is empty,
 * even if a debounced value from the previous query is still pending.
 */
export function useSearchQueryValue(value: string, delayMs = DEFAULT_DELAY_MS): string {
  const debounced = useDebouncedSearch(value, delayMs);
  return value.trim() === '' ? '' : debounced;
}
